import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { CollectionHelper, CompareHelper, ConvertHelper } from '@barthauer/abc/core';
import { ICoreDataEntity } from '@barthauer/abc/core/data';
import { ResourceManagerService, ResourceSet } from '@barthauer/abc/core/resources';
import { AbcExpressionsEditorService } from '@barthauer/abc/queries/opath/expressions-editor';
import { ParameterValue } from '@barthauer/abc/queries/parameters/core';
import { _AbcGridColumn, _AbcGridComponent } from '@barthauer/abc/ui-components';
import { DataSource, ArrayStore } from 'devextreme/common/data';
import { AIColumnRequestCreatingEvent, EditorPreparingEvent, Row } from 'devextreme/ui/data_grid';
import {
  ColumnUpdateArgs,
  convertDataType,
  GridColumnDataType,
} from 'libs/abc/ui-components/src/lib/core/src/abc-grid/abc-grid';
import { AbcGridContextMenuManager } from 'libs/abc/ui-components/src/lib/core/src/abc-grid/abc-grid-context-menu-manager';
import { ObjectsSetsSettings } from 'libs/abc/ui-frameworks/objects-sets/src/lib/objects-sets-column-info';
import { ObjectsSetsComponent } from 'libs/abc/ui-frameworks/objects-sets/src/lib/objects-sets.component';
import { Subscription } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { AbcArenaService } from '../../abc-arena.service';
import { ArenaSetting } from '../../models/arena-settings.model';
import { ArenaSettingsService } from '../../services/arena-settings.service';
import { DataService } from '../../services/data.service';
import { ArenaService } from '../arena.service';
import { LayoutColumnViewModel, LayoutViewModel } from '../viewModels/layout.viewModel';
import { HignaService } from './higna.service';
import { SpecialType } from '../models/layout.model';

@Component({
  selector: 'arena-higna',
  templateUrl: './higna.component.html',
  styleUrls: ['./higna.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class HignaComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('abcGridContainer', { static: true }) abcGrid: _AbcGridComponent;
  @ViewChild('objectsSets', { static: false, read: ViewContainerRef }) vcr: ViewContainerRef;
  hignaLoaderContainerRef: ViewContainerRef;

  @Output() onObjectsSelected: EventEmitter<ICoreDataEntity[]> = new EventEmitter();

  public resAsync: Promise<ResourceSet>;
  public res = ResourceSet.Empty;
  private subscriptions: Subscription[] = [];
  private layoutViewModel: LayoutViewModel;
  private layoutFilter: string;
  private layoutObjectIds: string[];
  private layoutParameterValues: ParameterValue[];
  public rtlEnabled: boolean;
  private getCustomByIdFilters: () => Map<string, string[]>;

  public get renderHignaButtons(): boolean {
    return this.arenaService.uiconfig.renderHignaButtons;
  }

  public get dataSource(): any[] {
    if (this.abcGrid.dataSource instanceof DataSource) {
      return this.abcGrid.dataSource.items();
    }
    return this.abcGrid.dataSource as any[];
  }

  constructor(
    private arenaService: ArenaService,
    private hignaService: HignaService,
    private dataService: DataService,
    private abcArenaService: AbcArenaService,
    private changeDetectorRef: ChangeDetectorRef,
    private arenaSettingsService: ArenaSettingsService,
    private resourceService: ResourceManagerService,
    private opathExpressionEditorService: AbcExpressionsEditorService,
  ) {
    this.resAsync = this.resourceService.getResourceSet('abc-arena-higna');
    this.rtlEnabled = arenaService.rtlEnabled;
  }

  ngAfterViewInit(): void {}

  async ngOnInit(): Promise<void> {
    this.res = await this.resAsync;

    this.subscriptions.push(
      this.arenaService.hignaScrollToTop$.subscribe(() => {
        this.abcGrid.scrollToTop();
      }),
    );

    this.subscriptions.push(
      this.abcGrid.onColumnUpdate
        .pipe(
          mergeMap(async (event: ColumnUpdateArgs) => {
            await this.changeColumnVisibility(event.column, event.visibility);
          }),
        )
        .subscribe(() => {}),
    );
  }

  public async loadLayout(
    layoutViewModel: LayoutViewModel,
    filter: string,
    objectIds: string[],
    parameterValues: ParameterValue[],
    currentId: string,
    cachedGridObjects: unknown[],
    preserveFilter: boolean,
    getCustomByIdFilters: () => Map<string, string[]>,
  ): Promise<HignaDataSource> {
    this.layoutViewModel = layoutViewModel;
    this.layoutFilter = filter;
    this.layoutObjectIds = objectIds;
    this.layoutParameterValues = parameterValues;
    this.getCustomByIdFilters = getCustomByIdFilters;

    if (!layoutViewModel) {
      return;
    }
    this.resetToInitialGridObjects = null;
    this.isFilteredByContextObjects = false;

    this.abcGrid.storageSettings.storage = this.arenaSettingsService.createStorage(ArenaSetting.gridLayouts);

    //Ensure that old layout is saved
    if (this.abcGrid.columns?.length) {
      await this.abcGrid.saveLayout();
    }

    this.abcGrid.storageSettings.includeFiltering = preserveFilter;

    const gridObjects = this.setDatasource(
      layoutViewModel,
      filter,
      objectIds,
      parameterValues,
      currentId,
      cachedGridObjects,
      preserveFilter,
      getCustomByIdFilters,
    );

    const dataSource = {
      gridObjects: gridObjects,
    };

    this.abcGrid.getCustomContext = async () => {
      return {
        opathExpressionEditor: this.opathExpressionEditorService,
        objectType: this.layoutViewModel.objectType,
        loadParameterDefinitions: async () => {
          return await this.arenaService.loadParameterDefinitions();
        },
        saveParameterDefinitions: async (parameterDefinitions) => {
          await this.arenaService.saveParameterDefinitions(parameterDefinitions);
        },
      };
    };

    this.abcGrid.refreshCustomColumn = async (customColumn) => {
      this.layoutViewModel.refreshCustomColumn(customColumn);
      for (const c of this.abcGrid.columns) {
        if (c.name === customColumn.name) {
          c[AbcGridContextMenuManager.CustomOPathColumnKey] = customColumn.opath;
          c.caption = customColumn.caption;
        }
      }
      const wasIncludeFiltering = this.abcGrid.storageSettings.includeFiltering;
      this.abcGrid.storageSettings.includeFiltering = true;

      await this.abcGrid.saveLayout();

      parameterValues = await this.arenaService.evaluateLayoutParameters(
        layoutViewModel,
        filter,
        await this.arenaService.loadParameterDefinitions(),
        this.layoutParameterValues,
      );

      const newGridObjects = await this.setDatasource(
        layoutViewModel,
        filter,
        objectIds,
        parameterValues,
        this.abcGrid.focusedRowKey,
        null,
        true,
        getCustomByIdFilters,
      );
      dataSource.gridObjects = Promise.resolve(newGridObjects);

      if (wasIncludeFiltering !== this.abcGrid.storageSettings.includeFiltering) {
        this.abcGrid.storageSettings.includeFiltering = wasIncludeFiltering;
      }
    };

    this.abcGrid.deleteCustomColumn = async (columnHeaderName) => {
      this.abcGrid.deleteColumn(columnHeaderName);
      this.layoutViewModel.removeCustomColumn(columnHeaderName);
      await this.abcGrid.saveLayout();
    };

    this.abcGrid.layoutReset = async () => {
      this.layoutViewModel.resetVisibleGridColumns();
      this.abcGrid.columns = [];
      const newDataSource = await this.loadLayout(
        this.layoutViewModel,
        this.layoutFilter,
        this.layoutObjectIds,
        this.layoutParameterValues,
        this.currentDataEntity?.Id,
        undefined,
        false,
        this.getCustomByIdFilters,
      );
      dataSource.gridObjects = newDataSource?.gridObjects;
    };

    if (await ObjectsSetsSettings?.getCurrentContainerId) {
      this.abcGrid.objectsSetsInstance = () => this.showObjectsSets();
    }

    return dataSource;
  }

  private async setDatasource(
    layoutViewModel: LayoutViewModel,
    filter: string,
    objectIds: string[],
    parameterValues: ParameterValue[],
    currentId: string,
    cachedGridObjects: unknown[],
    preserveFilter: boolean,
    getCustomByIdFilters: () => Map<string, string[]>,
  ): Promise<unknown[]> {
    const storageKey = this.getStateStorageKey(layoutViewModel);

    await this.abcGrid.setDataSource(
      this.createDataSource([]),
      async () => {
        for (const c of await this.abcGrid.loadCustomColumnsFromLayout()) {
          layoutViewModel?.refreshCustomColumn(c);
        }
        return await this.prepareGridColumns(this.layoutViewModel?.gridColumns);
      },
      storageKey,
    );
    const gridObjects =
      cachedGridObjects ??
      ConvertHelper.arrayBufferToObjectArray(
        await this.dataService
          .readGridObjects(
            this.arenaService.arenaName,
            layoutViewModel,
            filter ? [filter] : null,
            this.abcGrid,
            objectIds,
            undefined,
            parameterValues,
          )
          .toPromise(),
      );
    await this.prepareGridObjectsAsync(layoutViewModel, gridObjects);
    this.abcGrid.dataSource = this.createDataSource(gridObjects);
    this.prepareGridColumnsFromData(gridObjects);
    const customFilterByIds = getCustomByIdFilters ? getCustomByIdFilters() : undefined;
    if (customFilterByIds?.size) {
      const ids = CollectionHelper.distinctValues(CollectionHelper.flatten(Array.from(customFilterByIds.values())));
      this.abcGrid.filterByRowKeys(ids, Array.from(customFilterByIds.keys()).join(', '), preserveFilter);
    }

    if (!currentId && gridObjects?.length) {
      currentId = gridObjects[0][this.hignaService.IdColumnName];
    }
    if (currentId) {
      await this.setFocusedRow(currentId);
    }

    if (objectIds?.length) {
      this.isFilteredByContextObjects = true;
      this.resetToInitialGridObjects = async (withoutFilter: boolean) => {
        await this.setDatasource(
          layoutViewModel,
          filter,
          withoutFilter ? null : objectIds,
          parameterValues,
          currentId,
          withoutFilter ? null : gridObjects,
          preserveFilter,
          getCustomByIdFilters,
        );
      };
    }

    return gridObjects;
  }

  private async prepareGridObjectsAsync(layoutViewModel: LayoutViewModel, gridObjects: unknown[]): Promise<void> {
    if (layoutViewModel && gridObjects?.length) {
      for (const c of layoutViewModel.allColumns) {
        if (c.specialType === SpecialType.ObjectTypeDesignation) {
          for (const gridObject of gridObjects) {
            let o: any = gridObject;
            if (o && !CompareHelper.isNullorEmpty(o[c.dataField])) {
              o[c.dataField] = await this.arenaService.getObjectTypeTextAsync(o[c.dataField]);
            }
          }
        }
      }
    }
  }

  private prepareGridColumnsFromData(gridObjects: unknown[]) {
    if (this.abcGrid.columns?.length && gridObjects?.length) {
      for (const c of this.abcGrid.columns) {
        if (
          c[AbcGridContextMenuManager.CustomOPathColumnKey] &&
          c.dataType === convertDataType(GridColumnDataType.String)
        ) {
          for (const gridObject of gridObjects) {
            const o: any = gridObject;
            if (o && !CompareHelper.isNullorEmpty(o[c.dataField])) {
              const v = o[c.dataField];
              if (typeof v === 'number') {
                if (v != Math.round(v)) {
                  c.dataType = convertDataType(GridColumnDataType.Double);
                  c.format = _AbcGridComponent.getColumnFormat(c.dataType, '##########0.0#####');
                  this.abcGrid.instance?.columnOption(c.name, 'dataType', c.dataType);
                  break;
                } else if (c.dataType !== convertDataType(GridColumnDataType.Integer32)) {
                  c.dataType = convertDataType(GridColumnDataType.Integer32);
                  c.format = _AbcGridComponent.getColumnFormat(c.dataType, '##########0');
                  this.abcGrid.instance?.columnOption(c.name, 'dataType', c.dataType);
                }
              }
            }
          }
        }
      }
    }
  }

  private getStateStorageKey(layoutViewModel: LayoutViewModel) {
    return this.dataService.getStateStorageKey(this.arenaService.arenaName, layoutViewModel?.id);
  }

  private createDataSource(gridObjects: unknown[]): DataSource<any, any> {
    return new DataSource(
      new ArrayStore({
        key: this.hignaService.IdColumnName,
        data: gridObjects,
      }),
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    // this.addRemoveColumnsService.clearListCheckedElements();
  }

  public onEditorPreparing(event: EditorPreparingEvent): void {
    //Disable cell editing for new row
    if (event.row?.isNewRow) {
      event.editorOptions.disabled = true;
    }
  }

  private currentDataEntity: ICoreDataEntity;
  public async notifyCurrentObject(entity: ICoreDataEntity): Promise<void> {
    this.abcGrid.storageSettings.includeFiltering = false;
    this.currentDataEntity = entity;
    this.shareCurrentObjectCore();
  }

  private resetToInitialGridObjects: (withoutFilter: boolean) => Promise<void>;
  public isFilteredByContextObjects: boolean;
  public async filterByContextObjects(withoutFilter: boolean): Promise<void> {
    if (this.resetToInitialGridObjects) {
      await this.resetToInitialGridObjects(withoutFilter);
      this.isFilteredByContextObjects = !withoutFilter;
    }
  }

  public canFilterByContextObjects(): boolean {
    return !!this.resetToInitialGridObjects;
  }

  public async shareCurrentObjects(): Promise<void> {
    const dataEntities = await this.getFilteredDataEntities();
    this.onObjectsSelected.emit(dataEntities);
    this.changeDetectorRef.detectChanges();
  }

  public isSharingCurrentObject: boolean;
  public async shareCurrentObject(deactivate: boolean): Promise<void> {
    this.isSharingCurrentObject = !deactivate;
    await this.shareCurrentObjectCore();
    this.changeDetectorRef.detectChanges();
  }

  private shareCurrentObjectCore(): void {
    if (this.currentDataEntity?.Id && this.isSharingCurrentObject) {
      this.onObjectsSelected.emit([this.currentDataEntity]);
    }
  }

  public async addEmptyRowToGrid(): Promise<void> {
    this.abcGrid.focusedRowKey = null;
    await this.abcGrid.instance.addRow();
  }

  public async updateGridObjectById(id: string) {
    const gridObject: ICoreDataEntity = ConvertHelper.arrayBufferToObject(
      await this.dataService
        .readGridObjects(this.arenaService.arenaName, this.layoutViewModel, null, this.abcGrid, [id])
        .toPromise(),
    );
    await this.prepareGridObjectsAsync(this.layoutViewModel, [gridObject]);

    await this.abcGrid.instance.getDataSource().store().update(gridObject?.Id, gridObject);
    return gridObject;
  }

  public async insertGridObjectById(id: string): Promise<ICoreDataEntity> {
    this.abcGrid.instance.cancelEditData();

    const gridObject: ICoreDataEntity = ConvertHelper.arrayBufferToObject(
      await this.dataService
        .readGridObjects(this.arenaService.arenaName, this.layoutViewModel, null, this.abcGrid, [id])
        .toPromise(),
    );
    await this.prepareGridObjectsAsync(this.layoutViewModel, [gridObject]);

    await this.abcGrid.instance.getDataSource().store().insert(gridObject);
    await this.abcGrid.instance.getDataSource().reload();
    this.setFocusedRow(gridObject.Id);
    return gridObject;
  }

  public async undoEditGridObject(id: string): Promise<void> {
    this.abcGrid.instance.cancelEditData();

    if (id && this.abcGrid.focusedRowKey != id) {
      await this.setFocusedRow(id);
    }
  }

  public async removeGridObjectById(id: string): Promise<number> {
    const ds = this.abcGrid.instance.getDataSource();
    await ds.store().remove(id);
    await ds.reload();
    return ds.items().length;
  }

  public async navigateToGridObjectById(id: string): Promise<void> {
    for (const row of this.dataSource) {
      if (row[this.hignaService.IdColumnName] == id) {
        await this.setFocusedRow(id);
        return;
      }
    }
    await this.insertGridObjectById(id);
  }

  private async prepareGridColumns(layoutViewModelColumns: LayoutColumnViewModel[]): Promise<_AbcGridColumn[]> {
    return this.hignaService.getLayoutTransferedGridColumns(layoutViewModelColumns);
  }

  public async ensureRowVisible(id: string): Promise<void> {
    this.abcGrid.ensureRowVisible(id);
  }

  public async setFocusedRow(id: string): Promise<void> {
    this.abcGrid.focusedRowKey = id;
    await this.ensureRowVisible(id);
  }

  public async getFilteredDataEntities(): Promise<ICoreDataEntity[]> {
    const result: ICoreDataEntity[] = [];
    for (const row of await this.abcGrid.getFilteredObjects()) {
      result.push(row);
    }
    return result;
  }

  public async showHideColumn(column: LayoutColumnViewModel): Promise<void> {
    if (this.isChangingColumnVisibility) {
      return;
    }

    const gridColumn = this.abcGrid?.columns?.find((_) => _.dataField === column?.dataField);
    if (gridColumn) {
      const visible = this.abcGrid.instance.columnOption(gridColumn.name, 'visible');
      const visibleIndex = this.abcGrid.instance.columnOption(gridColumn.name, 'visibleIndex');
      if (visible && visibleIndex >= 0) {
        gridColumn.visible = false;
      } else {
        gridColumn.visible = true;
        if ((gridColumn.visibleIndex ?? -1) < 0) {
          gridColumn.visibleIndex = this.abcGrid.columns?.length ?? 0;
        }
      }
      this.abcGrid.instance.columnOption(gridColumn.name, 'visibleIndex', gridColumn.visibleIndex);
      this.abcGrid.instance.columnOption(gridColumn.name, 'visible', gridColumn.visible);
      await this.abcGrid.instance.refresh();
      await this.changeColumnVisibility(gridColumn, !column.visible);
    }
  }

  private isChangingColumnVisibility: boolean = false;
  private async changeColumnVisibility(column: _AbcGridColumn, visibility: boolean): Promise<void> {
    if (!this.isChangingColumnVisibility) {
      try {
        this.isChangingColumnVisibility = true;
        this.layoutViewModel.updateColumnVisibility(column.dataField, column.visibleIndex, visibility);
        if (visibility) {
          const gridObjects = ConvertHelper.arrayBufferToObjectArray(
            await this.dataService
              .readGridObjects(
                this.arenaService.arenaName,
                this.layoutViewModel,
                this.layoutFilter ? [this.layoutFilter] : [],
                this.abcGrid,
                this.layoutObjectIds,
                true,
                this.layoutParameterValues,
              )
              .toPromise(),
          );
          await this.prepareGridObjectsAsync(this.layoutViewModel, gridObjects);
          this.abcGrid.dataSource = this.createDataSource(gridObjects);
          this.prepareGridColumnsFromData(gridObjects);
        }
      } finally {
        this.isChangingColumnVisibility = false;
      }
    }
  }

  private async showObjectsSets() {
    const coreDataEntities = this.abcGrid.instance.getDataSource()?.items();

    if (coreDataEntities?.length) {
      const s = this.vcr.createComponent(ObjectsSetsComponent);
      const osc: ObjectsSetsComponent = <ObjectsSetsComponent>s?.instance;

      if (osc) {
        const objectType = coreDataEntities[0]?.ObjectType;
        osc.parentObjectType = objectType;

        osc.sourceDataEntities = <ICoreDataEntity>() => {
          return {
            objectType: objectType,
            entities: coreDataEntities as ICoreDataEntity[],
            idExpression: (id) => (id = this.hignaService.IdColumnName),
            objectTypeExpression: (ot) => (ot = this.hignaService.ObjectTypeColumnName),
          };
        };
        osc.loadObjectsSetsWindow();
        osc.onSelectEntities = async (object) => {
          const entities = object?.entities;
          const ids = entities.map((entity) => entity.Id);
          this.abcGrid.filterByRowKeys(ids);
        };
      }
    }
  }
  public handleAIColumnRequestCreating(e: AIColumnRequestCreatingEvent) {
    const visibleRows = this.abcGrid.instance.getVisibleRows() as RowWithDataIndex[];
    const items = this.abcGrid.instance.getDataSource().items().splice(visibleRows[0].dataIndex, 20);
    const cleaned = items.map((row) => {
      delete row.ObjectType;
      return row;
    });
    e.data = cleaned;
  }
}

interface RowWithDataIndex extends Row {
  dataIndex: number;
}

interface HignaDataSource {
  gridObjects: Promise<unknown[]>;
}
